import { useInfiniteQuery } from "@tanstack/react-query";

// Keyset-paginated lists (GET /v1/campaigns, /v1/sites, /v1/payouts,
// /v1/refunds, /v1/wallet/deposits, ...): every one answers
// {<rows>: [...], next_cursor: string | null}, newest first, with an
// opaque cursor (campaign_read.go:23-43; wallet.go:114-194 in 0056;
// publisher_reads.go:40-72 in 0057). The first page sends no cursor.

export interface CursorPage {
  next_cursor: string | null;
}

export interface CursorListResult<Row> {
  query: {
    isPending: boolean;
    isError: boolean;
    isFetchNextPageError: boolean;
    error: Error | null;
    refetch: () => unknown;
  };
  rows: Row[];
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
}

export function useCursorList<P extends CursorPage, Row>(
  queryKey: readonly unknown[],
  fetchPage: (cursor: string | null) => Promise<P>,
  rowsOf: (page: P) => Row[],
  opts: { enabled?: boolean; refetchInterval?: number | false } = {},
): CursorListResult<Row> {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last: P) => last.next_cursor ?? undefined,
    enabled: opts.enabled,
    refetchInterval: opts.refetchInterval,
  });
  const rows: Row[] = query.data ? query.data.pages.flatMap(rowsOf) : [];
  return {
    query,
    rows,
    hasMore: !!query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
  };
}
